import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Compass,
  Settings,
  TrendingUp,
  Package,
  Database,
  ArrowRight,
  Sparkles,
  Users,
  Clock,
  Building2,
  CheckCircle2,
  Target,
  FileText,
  BarChart3,
  Zap,
  Shield,
  Lightbulb,
  Globe,
  Layers,
  RefreshCw,
  X,
} from "lucide-react";

// Product data
const products = [
  {
    id: "fundmatch",
    name: "FundMatch",
    tagline: "Grant Discovery",
    description: "AI-powered probabilistic matching across 600+ grant programs with 8-measures confidence dashboard.",
    icon: Compass,
    color: "teal",
    features: ["3-tier matching pipeline", "Near-miss alerts", "Repositioning suggestions"],
    preview: {
      title: "FundMatch Discovery",
      items: [
        { name: "Toronto Community Foundation", range: "$50K-$150K", score: 94 },
        { name: "Ontario Trillium Foundation", range: "$25K-$200K", score: 87 },
        { name: "Canada Council for the Arts", range: "$10K-$100K", score: 81 },
        { name: "United Way Greater Toronto", range: "$15K-$75K", score: 76 },
      ],
    },
  },
  {
    id: "adminhub",
    name: "AdminHub",
    tagline: "Administration",
    description: "Automate outreach, track applications, manage compliance, and generate AI-assisted reports.",
    icon: Settings,
    color: "amber",
    features: ["AI-drafted outreach", "Compliance tracking", "Follow-up automation"],
    preview: {
      title: "AdminHub Actions",
      items: [
        { name: "Q4 Progress Report Due", type: "urgent", days: 5 },
        { name: "Follow up with OTF", type: "high", days: 2 },
        { name: "Draft outreach ready", type: "ready", days: 0 },
        { name: "Application deadline", type: "medium", days: 14 },
      ],
    },
  },
  {
    id: "impactgrow",
    name: "ImpactGrow",
    tagline: "Impact Growth",
    description: "Quantify and communicate your social impact across individual, government, business, and community lenses.",
    icon: TrendingUp,
    color: "cyan",
    features: ["4-lens framework", "Impact scoring", "CSR partnerships"],
    preview: {
      title: "Impact Score",
      score: 78,
      level: "Growing",
      lenses: [
        { name: "Individual", score: 82 },
        { name: "Government", score: 75 },
        { name: "Business", score: 71 },
        { name: "Community", score: 84 },
      ],
    },
  },
  {
    id: "resourcehub",
    name: "ResourceHub",
    tagline: "Resources",
    description: "Access free software worth $25K+/year and connect with 83,000+ organizations for partnerships.",
    icon: Package,
    color: "violet",
    features: ["500+ free tools", "Partnership matching", "$25K+ savings"],
    preview: {
      title: "Top Resources",
      items: [
        { name: "Google for Nonprofits", value: "$2,400/yr", recommended: true },
        { name: "Microsoft 365", value: "$3,600/yr", recommended: true },
        { name: "Salesforce NPSP", value: "$18,000/yr", recommended: false },
        { name: "Canva Pro", value: "$156/yr", recommended: true },
      ],
    },
  },
  {
    id: "dataintel",
    name: "DataIntel",
    tagline: "Data Intelligence",
    description: "Comprehensive database of 83,000+ Canadian nonprofits with ML-ready features and insights.",
    icon: Database,
    color: "blue",
    features: ["83K+ entities", "22 ML features", "Real-time insights"],
    preview: {
      title: "Data Coverage",
      stats: [
        { label: "Entities", value: "83,301" },
        { label: "Grant Programs", value: "605" },
        { label: "Unique Givers", value: "38,654" },
        { label: "Data Freshness", value: "94%" },
      ],
    },
  },
];

const steps = [
  {
    number: "01",
    title: "Connect Your Profile",
    description: "Import your T3010 data or create a profile. We automatically extract your L1-L4 goal hierarchy.",
    icon: Users,
    color: "teal",
  },
  {
    number: "02",
    title: "Discover Opportunities",
    description: "FundMatch surfaces grants ranked by 8 measures with confidence intervals. See near-misses too.",
    icon: Target,
    color: "amber",
  },
  {
    number: "03",
    title: "Automate Admin",
    description: "AI drafts outreach, tracks compliance, and manages follow-ups. Save 10+ hours per week.",
    icon: Zap,
    color: "cyan",
  },
  {
    number: "04",
    title: "Grow Impact",
    description: "Measure outcomes across 4 lenses, build partnerships, and tell your impact story with data.",
    icon: TrendingUp,
    color: "violet",
  },
];

const stats = [
  { icon: Globe, value: "83K+", label: "Canadian Entities" },
  { icon: FileText, value: "600+", label: "Grant Programs" },
  { icon: Clock, value: "<60s", label: "Discovery Time" },
];

// Product Preview Components
const FundMatchPreview = ({ items }) => (
  <div className="space-y-3">
    {items.map((item, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: i * 0.1 }}
        className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/50"
      >
        <div>
          <p className="font-medium text-white text-sm">{item.name}</p>
          <p className="text-xs text-slate-400">{item.range}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full"
              style={{ width: `${item.score}%` }}
            />
          </div>
          <span className="text-teal-400 font-bold text-sm">{item.score}%</span>
        </div>
      </motion.div>
    ))}
    <p className="text-xs text-slate-500 flex items-center gap-1 mt-2">
      <Sparkles className="h-3 w-3" /> 8 measures calculated with confidence intervals
    </p>
  </div>
);

const AdminHubPreview = ({ items }) => (
  <div className="space-y-3">
    {items.map((item, i) => {
      const colors = {
        urgent: "bg-red-500/20 text-red-400 border-red-500/30",
        high: "bg-amber-500/20 text-amber-400 border-amber-500/30",
        ready: "bg-teal-500/20 text-teal-400 border-teal-500/30",
        medium: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      };
      return (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1 }}
          className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/50"
        >
          <div className="flex items-center gap-3">
            <div className={`px-2 py-0.5 rounded text-xs font-medium ${colors[item.type]}`}>
              {item.type}
            </div>
            <p className="font-medium text-white text-sm">{item.name}</p>
          </div>
          <span className="text-xs text-slate-400">{item.days === 0 ? "Ready" : `${item.days} days`}</span>
        </motion.div>
      );
    })}
  </div>
);

const ImpactGrowPreview = ({ score, level, lenses }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-4xl font-bold text-white">{score}</p>
        <p className="text-sm text-slate-400">Overall Score</p>
      </div>
      <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">{level}</Badge>
    </div>
    <div className="space-y-2">
      {lenses.map((lens, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1 }}
          className="flex items-center justify-between"
        >
          <span className="text-sm text-slate-300">{lens.name}</span>
          <div className="flex items-center gap-2">
            <div className="w-24 h-1.5 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-500 to-teal-400 rounded-full"
                style={{ width: `${lens.score}%` }}
              />
            </div>
            <span className="text-xs text-slate-400 w-8">{lens.score}</span>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const ResourceHubPreview = ({ items }) => (
  <div className="space-y-3">
    {items.map((item, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: i * 0.1 }}
        className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/50"
      >
        <div className="flex items-center gap-3">
          {item.recommended && <Sparkles className="h-3 w-3 text-amber-400" />}
          <p className="font-medium text-white text-sm">{item.name}</p>
        </div>
        <span className="text-teal-400 font-medium text-sm">{item.value}</span>
      </motion.div>
    ))}
  </div>
);

const DataIntelPreview = ({ stats }) => (
  <div className="grid grid-cols-2 gap-3">
    {stats.map((stat, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.1 }}
        className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50 text-center"
      >
        <p className="text-xl font-bold text-white">{stat.value}</p>
        <p className="text-xs text-slate-400">{stat.label}</p>
      </motion.div>
    ))}
  </div>
);

export const LandingPage = () => {
  const [activeProduct, setActiveProduct] = useState("fundmatch");
  const currentProduct = products.find((p) => p.id === activeProduct);

  const renderPreview = (product) => {
    switch (product.id) {
      case "fundmatch":
        return <FundMatchPreview items={product.preview.items} />;
      case "adminhub":
        return <AdminHubPreview items={product.preview.items} />;
      case "impactgrow":
        return <ImpactGrowPreview {...product.preview} />;
      case "resourcehub":
        return <ResourceHubPreview items={product.preview.items} />;
      case "dataintel":
        return <DataIntelPreview stats={product.preview.stats} />;
      default:
        return null;
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section - Dark Theme */}
      <section className="relative min-h-screen bg-slate-900" data-testid="hero-section">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-teal-900/20 via-transparent to-transparent" />

        {/* Decorative Elements */}
        <div className="absolute top-1/4 right-10 w-16 h-16 text-amber-400 opacity-50">
          <Sparkles className="w-full h-full" />
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Badge className="mb-6 bg-teal-500/20 text-teal-400 border border-teal-500/30 px-4 py-1.5 text-sm font-medium">
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  AI-Powered Grant Intelligence for Canadian Nonprofits
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white tracking-tight mb-6"
              >
                The complete{" "}
                <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-teal-300 bg-clip-text text-transparent">
                  grant intelligence
                </span>{" "}
                platform
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg text-slate-400 leading-relaxed mb-8 max-w-lg"
              >
                Impact Plexus surfaces funding opportunities, automates funder interactions, 
                and measures impact — so nonprofits can focus on their mission.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/dashboard">
                  <Button
                    size="lg"
                    className="bg-teal-500 hover:bg-teal-600 text-white font-medium px-8 py-6 text-base shadow-lg shadow-teal-500/20"
                    data-testid="hero-cta"
                  >
                    Start discovering grants
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex items-center gap-8 mt-12 pt-8 border-t border-slate-800"
              >
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <stat.icon className="h-5 w-5 text-slate-500 mx-auto mb-2" />
                    <p className="text-2xl font-heading font-bold text-white">{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Right - Product Preview Panel */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 shadow-2xl">
                {/* Panel Header */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <div className="w-3 h-3 rounded-full bg-teal-500" />
                  </div>
                  <span className="text-sm text-slate-400 ml-2">{currentProduct?.preview.title}</span>
                </div>

                {/* Preview Content */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeProduct}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {currentProduct && renderPreview(currentProduct)}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section - Tabbed */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="products-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-slate-200 text-slate-700 hover:bg-slate-200 border-0">
              Full-Lifecycle Platform
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 tracking-tight mb-4">
              Five products, one mission
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Click any product to explore. Each is designed to work seamlessly together.
            </p>
          </div>

          {/* Product Tabs */}
          <Tabs value={activeProduct} onValueChange={setActiveProduct} className="w-full">
            <TabsList className="w-full flex flex-wrap justify-center gap-2 bg-transparent h-auto mb-12">
              {products.map((product) => {
                const colorClasses = {
                  teal: "data-[state=active]:bg-teal-500 data-[state=active]:text-white",
                  amber: "data-[state=active]:bg-amber-500 data-[state=active]:text-white",
                  cyan: "data-[state=active]:bg-cyan-500 data-[state=active]:text-white",
                  violet: "data-[state=active]:bg-violet-500 data-[state=active]:text-white",
                  blue: "data-[state=active]:bg-blue-500 data-[state=active]:text-white",
                };
                return (
                  <TabsTrigger
                    key={product.id}
                    value={product.id}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 bg-white hover:border-slate-300 transition-all ${colorClasses[product.color]}`}
                    data-testid={`product-tab-${product.id}`}
                  >
                    <product.icon className="h-4 w-4" />
                    <span className="font-medium">{product.name}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {products.map((product) => (
              <TabsContent key={product.id} value={product.id} className="mt-0">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12 shadow-sm"
                >
                  <div className="grid md:grid-cols-2 gap-12 items-center">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-12 h-12 rounded-xl bg-${product.color}-100 flex items-center justify-center`}>
                          <product.icon className={`h-6 w-6 text-${product.color}-600`} />
                        </div>
                        <div>
                          <h3 className="text-2xl font-heading font-bold text-slate-900">{product.name}</h3>
                          <p className="text-sm text-slate-500">{product.tagline}</p>
                        </div>
                      </div>
                      <p className="text-lg text-slate-600 mb-6">{product.description}</p>
                      <ul className="space-y-3 mb-8">
                        {product.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-3 text-slate-700">
                            <CheckCircle2 className={`h-5 w-5 text-${product.color}-500`} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Link to={`/products/${product.id}`}>
                        <Button className={`bg-${product.color}-500 hover:bg-${product.color}-600 text-white`}>
                          Learn more about {product.name}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>

                    {/* Preview Panel */}
                    <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                          <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                          <div className="w-2.5 h-2.5 rounded-full bg-teal-500" />
                        </div>
                        <span className="text-xs text-slate-500 ml-2">{product.preview.title}</span>
                      </div>
                      {renderPreview(product)}
                    </div>
                  </div>
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="how-it-works-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100 border-0">
              How It Works
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 tracking-tight mb-4">
              From onboarding to impact,<br />
              <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                in four steps
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const colorClasses = {
                teal: "bg-teal-100 text-teal-600 border-teal-200",
                amber: "bg-amber-100 text-amber-600 border-amber-200",
                cyan: "bg-cyan-100 text-cyan-600 border-cyan-200",
                violet: "bg-violet-100 text-violet-600 border-violet-200",
              };
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-slate-200 to-slate-200" />
                  )}

                  <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:border-slate-200 hover:shadow-lg transition-all relative">
                    <div className={`w-14 h-14 rounded-xl ${colorClasses[step.color]} border flex items-center justify-center mb-6`}>
                      <step.icon className="h-7 w-7" />
                    </div>
                    <div className="text-xs font-bold text-slate-400 mb-2">{step.number}</div>
                    <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">{step.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Built on Data Intelligence Section - Dark Theme */}
      <section className="py-20 md:py-32 bg-slate-900 relative overflow-hidden" data-testid="intelligence-section">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-slate-900" />
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-teal-500/20 text-teal-400 border border-teal-500/30">
              Under The Hood
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white tracking-tight mb-4">
              Built on data intelligence,<br />
              <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                powered by AI
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              The DataIntel platform processes T3010 filings, GrantConnect data, and 
              web intelligence to deliver unmatched funding insights.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Intelligence Feature Cards */}
            {[
              {
                icon: Layers,
                title: "3-Tier AI Matching",
                description: "Hard filters, 22-feature soft scoring, and semantic similarity with pgvector for precise grant matching.",
              },
              {
                icon: BarChart3,
                title: "8-Measure Analysis",
                description: "Every match scored on Opportunities, Expectations, Survey, Outlook, Leverage, Positioning, Efficiency, and Assurance.",
              },
              {
                icon: Database,
                title: "22-Feature ML Pipeline",
                description: "7-dimensional feature framework with configurable weights, normalization, and bind strength across all entity data.",
              },
              {
                icon: FileText,
                title: "L1-L4 Goal Hierarchy",
                description: "AI-extracted mission structure: WHY (stable), WHAT (semi-stable), HOW (contextual), WITH WHAT (dynamic).",
              },
              {
                icon: Zap,
                title: "Near-Miss Intelligence",
                description: "Identifies exactly why orgs miss eligibility and generates actionable repositioning strategies.",
              },
              {
                icon: RefreshCw,
                title: "Auto-Updating Data",
                description: "GrantConnect weekly syncs, Playwright crawls, and smart re-embedding keep all data current.",
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-teal-500/30 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-teal-500/20 border border-teal-500/30 flex items-center justify-center mb-5">
                  <feature.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="comparison-section">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-slate-100 text-slate-700 hover:bg-slate-100 border-0">
              Why Impact Plexus
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 tracking-tight mb-4">
              How we compare
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              See why leading Canadian nonprofits choose Impact Plexus over alternatives.
            </p>
          </div>

          {/* Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="overflow-x-auto"
          >
            <div className="min-w-[700px]">
              {/* Header Row */}
              <div className="grid grid-cols-5 gap-4 mb-6">
                <div className="col-span-1"></div>
                <div className="col-span-1 text-center">
                  <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl p-6 text-white">
                    <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center mx-auto mb-3">
                      <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                      </svg>
                    </div>
                    <p className="font-heading font-bold text-lg">Impact Plexus</p>
                  </div>
                </div>
                <div className="col-span-1 text-center">
                  <div className="bg-slate-100 rounded-2xl p-6">
                    <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center mx-auto mb-3">
                      <Globe className="w-5 h-5 text-slate-500" />
                    </div>
                    <p className="font-heading font-semibold text-slate-700">GrantConnect</p>
                  </div>
                </div>
                <div className="col-span-1 text-center">
                  <div className="bg-slate-100 rounded-2xl p-6">
                    <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center mx-auto mb-3">
                      <FileText className="w-5 h-5 text-slate-500" />
                    </div>
                    <p className="font-heading font-semibold text-slate-700">GrantStation</p>
                  </div>
                </div>
                <div className="col-span-1 text-center">
                  <div className="bg-slate-100 rounded-2xl p-6">
                    <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center mx-auto mb-3">
                      <Building2 className="w-5 h-5 text-slate-500" />
                    </div>
                    <p className="font-heading font-semibold text-slate-700">Manual Research</p>
                  </div>
                </div>
              </div>

              {/* Comparison Rows */}
              {[
                { feature: "AI-Powered Matching", us: true, gc: false, gs: false, mr: false },
                { feature: "8-Measures Dashboard", us: true, gc: false, gs: false, mr: false },
                { feature: "Near-Miss Alerts", us: true, gc: false, gs: false, mr: false },
                { feature: "Auto-Generated Outreach", us: true, gc: false, gs: false, mr: false },
                { feature: "Compliance Tracking", us: true, gc: false, gs: "partial", mr: false },
                { feature: "Impact Measurement", us: true, gc: false, gs: false, mr: false },
                { feature: "Canadian Entity Database", us: "83K+", gc: "Limited", gs: "US Focus", mr: "N/A" },
                { feature: "Grant Program Coverage", us: "600+", gc: "500+", gs: "5,000+", mr: "Variable" },
                { feature: "Weekly Data Sync", us: true, gc: true, gs: true, mr: false },
                { feature: "Free Software Directory", us: true, gc: false, gs: false, mr: false },
              ].map((row, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-5 gap-4 py-5 ${index % 2 === 0 ? 'bg-slate-50/50' : ''} rounded-xl`}
                >
                  <div className="col-span-1 flex items-center">
                    <span className="text-slate-700 font-medium pl-4">{row.feature}</span>
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    {row.us === true ? (
                      <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-teal-600" />
                      </div>
                    ) : row.us === false ? (
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <X className="w-4 h-4 text-slate-400" />
                      </div>
                    ) : (
                      <span className="text-teal-600 font-bold">{row.us}</span>
                    )}
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    {row.gc === true ? (
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-slate-600" />
                      </div>
                    ) : row.gc === false ? (
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <X className="w-4 h-4 text-slate-400" />
                      </div>
                    ) : row.gc === "partial" ? (
                      <span className="text-amber-500 text-sm">Partial</span>
                    ) : (
                      <span className="text-slate-500 text-sm">{row.gc}</span>
                    )}
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    {row.gs === true ? (
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-slate-600" />
                      </div>
                    ) : row.gs === false ? (
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <X className="w-4 h-4 text-slate-400" />
                      </div>
                    ) : row.gs === "partial" ? (
                      <span className="text-amber-500 text-sm">Partial</span>
                    ) : (
                      <span className="text-slate-500 text-sm">{row.gs}</span>
                    )}
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    {row.mr === true ? (
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-slate-600" />
                      </div>
                    ) : row.mr === false ? (
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <X className="w-4 h-4 text-slate-400" />
                      </div>
                    ) : (
                      <span className="text-slate-500 text-sm">{row.mr}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <p className="text-center text-xs text-slate-400 mt-8">
            Competitor information obtained from respective websites as of January 2026. Subject to change.
          </p>
        </div>
      </section>

      {/* CTA Section - Gradient Banner */}
      <section className="py-20 md:py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-teal-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-teal-800/30 via-transparent to-transparent" />

        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white tracking-tight mb-6">
              Ready to transform your funding?
            </h2>
            <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
              Join thousands of Canadian nonprofits using Impact Plexus to discover grants, 
              save time, and grow their impact.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/dashboard">
                <Button
                  size="lg"
                  className="bg-teal-500 hover:bg-teal-600 text-white font-medium px-8 py-6 text-base shadow-lg shadow-teal-500/20"
                  data-testid="cta-get-started"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-slate-600 text-white hover:bg-slate-800 font-medium px-8 py-6 text-base"
              >
                Book a Demo
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
